import { graphql } from 'react-relay';
import { useCallback } from 'react';
import type { MutationResult } from '@apollo/react-common';

import { ConfluencePageAri, ConfluenceBlogpostAri } from '@atlassian/ari';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import type {
	useUpdateContentPropertyPageCreateMutation$data,
	useUpdateContentPropertyPageCreateMutation$variables,
} from './__generated__/useUpdateContentPropertyPageCreateMutation.graphql.ts';
import type {
	useUpdateContentPropertyBlogCreateMutation$data,
	useUpdateContentPropertyBlogCreateMutation$variables,
} from './__generated__/useUpdateContentPropertyBlogCreateMutation.graphql.ts';
import type {
	useUpdateContentPropertyPageUpdateMutation$data,
	useUpdateContentPropertyPageUpdateMutation$variables,
} from './__generated__/useUpdateContentPropertyPageUpdateMutation.graphql.ts';
import type {
	useUpdateContentPropertyPageDeleteMutation$data,
	useUpdateContentPropertyPageDeleteMutation$variables,
} from './__generated__/useUpdateContentPropertyPageDeleteMutation.graphql.ts';
import type {
	useUpdateContentPropertyBlogUpdateMutation$data,
	useUpdateContentPropertyBlogUpdateMutation$variables,
} from './__generated__/useUpdateContentPropertyBlogUpdateMutation.graphql.ts';
import type {
	useUpdateContentPropertyBlogDeleteMutation$data,
	useUpdateContentPropertyBlogDeleteMutation$variables,
} from './__generated__/useUpdateContentPropertyBlogDeleteMutation.graphql.ts';

export const useUpdateContentProperty = ({
	contentId,
	contentType,
	options: queryOptions = {},
}: {
	contentId: string;
	contentType: string;
	options?: any;
}): {
	updateContentProperty: ({
		propertyKey,
		propertyValue,
		isDelete,
		hasExistingProperty,
		options,
		shouldOptimisticResponse,
	}: {
		propertyKey: string;
		propertyValue?: string;
		isDelete?: boolean;
		hasExistingProperty?: boolean;
		options?: any;
		shouldOptimisticResponse?: boolean;
	}) =>
		| Promise<{
				data?: useUpdateContentPropertyPageDeleteMutation$data | undefined;
		  }>
		| Promise<{
				data?: useUpdateContentPropertyBlogDeleteMutation$data | undefined;
		  }>
		| Promise<{
				data?: useUpdateContentPropertyPageUpdateMutation$data | undefined;
		  }>
		| Promise<{
				data?: useUpdateContentPropertyBlogUpdateMutation$data | undefined;
		  }>
		| Promise<{
				data?: useUpdateContentPropertyPageCreateMutation$data | undefined;
		  }>
		| Promise<{
				data?: useUpdateContentPropertyBlogCreateMutation$data | undefined;
		  }>
		| undefined;
	processUpdateContentPropertyResult: (
		result:
			| useUpdateContentPropertyPageCreateMutation$data
			| useUpdateContentPropertyBlogCreateMutation$data
			| useUpdateContentPropertyPageUpdateMutation$data
			| useUpdateContentPropertyBlogUpdateMutation$data
			| useUpdateContentPropertyPageDeleteMutation$data
			| useUpdateContentPropertyBlogDeleteMutation$data
			| undefined,
	) => {
		success?: boolean | null;
		id?: string | null;
		key?: string | null;
		value?: string | null;
		errors?: Array<{
			message?: string | null;
			extensions?: { statusCode?: number | null } | null;
		}> | null;
	} | null;
	updateContentPropertyResult: Pick<
		MutationResult<useUpdateContentPropertyPageCreateMutation$data>,
		'data' | 'error' | 'loading' | 'called'
	>;
} => {
	const { cloudId } = useSessionData();

	const [createPagePropertyMutation, createPagePropertyResult] = useMutationInterop<
		useUpdateContentPropertyPageCreateMutation$data,
		useUpdateContentPropertyPageCreateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyPageCreateMutation(
				$propertyKey: String!
				$pageId: ID!
				$propertyValue: String!
			) {
				confluence {
					createPageProperty(input: { key: $propertyKey, pageId: $pageId, value: $propertyValue }) {
						success
						pageProperty {
							id
							key
							value
						}
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [updatePagePropertyMutation, updatePagePropertyResult] = useMutationInterop<
		useUpdateContentPropertyPageUpdateMutation$data,
		useUpdateContentPropertyPageUpdateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyPageUpdateMutation(
				$propertyKey: String!
				$pageId: ID!
				$propertyValue: String!
			) {
				confluence {
					updateValuePageProperty(
						input: { key: $propertyKey, pageId: $pageId, value: $propertyValue }
					) {
						success
						pageProperty {
							id
							key
							value
						}
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [deletePagePropertyMutation, deletePagePropertyResult] = useMutationInterop<
		useUpdateContentPropertyPageDeleteMutation$data,
		useUpdateContentPropertyPageDeleteMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyPageDeleteMutation($propertyKey: String!, $pageId: ID!) {
				confluence {
					deletePageProperty(input: { key: $propertyKey, pageId: $pageId }) {
						success
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [createBlogPropertyMutation, createBlogPropertyResult] = useMutationInterop<
		useUpdateContentPropertyBlogCreateMutation$data,
		useUpdateContentPropertyBlogCreateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyBlogCreateMutation(
				$propertyKey: String!
				$blogPostId: ID!
				$propertyValue: String!
			) {
				confluence {
					createBlogPostProperty(
						input: { key: $propertyKey, blogPostId: $blogPostId, value: $propertyValue }
					) {
						success
						blogPostProperty {
							id
							key
							value
						}
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [updateBlogPropertyMutation, updateBlogPropertyResult] = useMutationInterop<
		useUpdateContentPropertyBlogUpdateMutation$data,
		useUpdateContentPropertyBlogUpdateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyBlogUpdateMutation(
				$propertyKey: String!
				$blogPostId: ID!
				$propertyValue: String!
			) {
				confluence {
					updateValueBlogPostProperty(
						input: { key: $propertyKey, blogPostId: $blogPostId, value: $propertyValue }
					) {
						success
						blogPostProperty {
							id
							key
							value
						}
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [deleteBlogPropertyMutation, deleteBlogPropertyResult] = useMutationInterop<
		useUpdateContentPropertyBlogDeleteMutation$data,
		useUpdateContentPropertyBlogDeleteMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyBlogDeleteMutation($propertyKey: String!, $blogPostId: ID!) {
				confluence {
					deleteBlogPostProperty(input: { key: $propertyKey, blogPostId: $blogPostId }) {
						success
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const updateContentProperty = useCallback(
		({
			propertyKey,
			propertyValue: propertyValueString,
			isDelete = false,
			hasExistingProperty = false,
			options = {},
			shouldOptimisticResponse = false,
		}: {
			propertyKey: string;
			propertyValue?: string;
			isDelete?: boolean;
			hasExistingProperty?: boolean;
			options?: any;
			shouldOptimisticResponse?: boolean;
		}):
			| Promise<{
					data?: useUpdateContentPropertyPageDeleteMutation$data | undefined;
			  }>
			| Promise<{
					data?: useUpdateContentPropertyBlogDeleteMutation$data | undefined;
			  }>
			| Promise<{
					data?: useUpdateContentPropertyPageUpdateMutation$data | undefined;
			  }>
			| Promise<{
					data?: useUpdateContentPropertyBlogUpdateMutation$data | undefined;
			  }>
			| Promise<{
					data?: useUpdateContentPropertyPageCreateMutation$data | undefined;
			  }>
			| Promise<{
					data?: useUpdateContentPropertyBlogCreateMutation$data | undefined;
			  }>
			| undefined => {
			if (isDelete) {
				if (contentType === 'page') {
					return deletePagePropertyMutation({
						variables: {
							pageId: ConfluencePageAri.create({
								siteId: cloudId,
								pageId: contentId,
							}).toString(),
							propertyKey,
						},
						...options,
						optimisticResponse: shouldOptimisticResponse
							? {
									success: true,
									errors: [],
								}
							: undefined,
					});
				} else if (contentType === 'blogpost') {
					return deleteBlogPropertyMutation({
						variables: {
							blogPostId: ConfluenceBlogpostAri.create({
								siteId: cloudId,
								blogpostId: contentId,
							}).toString(),
							propertyKey,
						},
						...options,
						optimisticResponse: shouldOptimisticResponse
							? {
									success: true,
									errors: [],
								}
							: undefined,
					});
				}
			} else {
				if (!!propertyValueString) {
					// Property value need to be stringified again before passing to graphql otherwise backend cannot parse it correctly
					const propertyValue = JSON.stringify(propertyValueString);
					if (hasExistingProperty) {
						// When there is existing property, we update it
						if (contentType === 'page') {
							return updatePagePropertyMutation({
								variables: {
									pageId: ConfluencePageAri.create({
										siteId: cloudId,
										pageId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											pageProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						} else if (contentType === 'blogpost') {
							return updateBlogPropertyMutation({
								variables: {
									blogPostId: ConfluenceBlogpostAri.create({
										siteId: cloudId,
										blogpostId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											blogPostProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						}
					} else {
						// When there is no existing property, we create it
						if (contentType === 'page') {
							return createPagePropertyMutation({
								variables: {
									pageId: ConfluencePageAri.create({
										siteId: cloudId,
										pageId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											pageProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						} else if (contentType === 'blogpost') {
							return createBlogPropertyMutation({
								variables: {
									blogPostId: ConfluenceBlogpostAri.create({
										siteId: cloudId,
										blogpostId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											blogPostProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						}
					}
				}
			}
		},
		[
			createPagePropertyMutation,
			createBlogPropertyMutation,
			updatePagePropertyMutation,
			updateBlogPropertyMutation,
			deletePagePropertyMutation,
			deleteBlogPropertyMutation,
			contentId,
			cloudId,
			contentType,
		],
	);

	const processUpdateContentPropertyResult = useCallback(
		(
			result:
				| useUpdateContentPropertyPageCreateMutation$data
				| useUpdateContentPropertyBlogCreateMutation$data
				| useUpdateContentPropertyPageUpdateMutation$data
				| useUpdateContentPropertyBlogUpdateMutation$data
				| useUpdateContentPropertyPageDeleteMutation$data
				| useUpdateContentPropertyBlogDeleteMutation$data
				| undefined,
		): {
			success?: boolean | null;
			id?: string | null;
			key?: string | null;
			value?: string | null;
			errors?: Array<{
				message?: string | null;
				extensions?: { statusCode?: number | null } | null;
			}> | null;
		} | null => {
			if (result && 'confluence' in result && result.confluence) {
				if ('createPageProperty' in result.confluence) {
					const { createPageProperty } = result.confluence;
					return {
						success: createPageProperty?.success,
						id: createPageProperty?.pageProperty?.id,
						key: createPageProperty?.pageProperty?.key,
						value: createPageProperty?.pageProperty?.value,
						errors: createPageProperty?.errors ? [...createPageProperty.errors] : undefined,
					};
				}
				if ('createBlogPostProperty' in result.confluence) {
					const { createBlogPostProperty } = result.confluence;
					return {
						success: createBlogPostProperty?.success,
						id: createBlogPostProperty?.blogPostProperty?.id,
						key: createBlogPostProperty?.blogPostProperty?.key,
						value: createBlogPostProperty?.blogPostProperty?.value,
						errors: createBlogPostProperty?.errors ? [...createBlogPostProperty.errors] : undefined,
					};
				}
				if ('updateValuePageProperty' in result.confluence) {
					const { updateValuePageProperty } = result.confluence;
					return {
						success: updateValuePageProperty?.success,
						id: updateValuePageProperty?.pageProperty?.id,
						key: updateValuePageProperty?.pageProperty?.key,
						value: updateValuePageProperty?.pageProperty?.value,
						errors: updateValuePageProperty?.errors
							? [...updateValuePageProperty.errors]
							: undefined,
					};
				}
				if ('updateValueBlogPostProperty' in result.confluence) {
					const { updateValueBlogPostProperty } = result.confluence;
					return {
						success: updateValueBlogPostProperty?.success,
						id: updateValueBlogPostProperty?.blogPostProperty?.id,
						key: updateValueBlogPostProperty?.blogPostProperty?.key,
						value: updateValueBlogPostProperty?.blogPostProperty?.value,
						errors: updateValueBlogPostProperty?.errors
							? [...updateValueBlogPostProperty.errors]
							: undefined,
					};
				}
				if ('deletePageProperty' in result.confluence) {
					const { deletePageProperty } = result.confluence;
					return {
						success: deletePageProperty?.success,
						errors: deletePageProperty?.errors ? [...deletePageProperty.errors] : undefined,
					};
				}
				if ('deleteBlogPostProperty' in result.confluence) {
					const { deleteBlogPostProperty } = result.confluence;
					return {
						success: deleteBlogPostProperty?.success,
						errors: deleteBlogPostProperty?.errors ? [...deleteBlogPostProperty.errors] : undefined,
					};
				}
			}
			return null;
		},
		[],
	);

	return {
		updateContentProperty,
		processUpdateContentPropertyResult,
		updateContentPropertyResult:
			createPagePropertyResult ||
			updatePagePropertyResult ||
			deletePagePropertyResult ||
			createBlogPropertyResult ||
			updateBlogPropertyResult ||
			deleteBlogPropertyResult,
	};
};
